import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
    Users, UserCheck, AlertTriangle, CheckCircle,
    TrendingUp, Clock, Activity
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAdminAxios = () => {
    const token = localStorage.getItem('admin_token');
    return axios.create({
        baseURL: API,
        headers: { Authorization: `Bearer ${token}` }
    });
};

export default function AdminDashboard() {
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [admin, setAdmin] = useState(null);

    useEffect(() => {
        const adminData = localStorage.getItem('admin_user');
        if (!adminData) {
            navigate('/admin/login');
            return;
        }
        setAdmin(JSON.parse(adminData));
        fetchStats();
    }, [navigate]);

    const fetchStats = async () => {
        try {
            const response = await getAdminAxios().get('/admin/dashboard/stats');
            setStats(response.data);
        } catch (err) {
            if (err.response?.status === 401) {
                localStorage.removeItem('admin_token');
                localStorage.removeItem('admin_user');
                navigate('/admin/login');
            }
            toast.error('Failed to load dashboard');
        } finally {
            setLoading(false);
        }
    };

    if (loading || !stats) {
        return (
            <div className="flex justify-center py-12">
                <div className="animate-spin w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full" />
            </div>
        );
    }

    return (
        <>
            <div className="mb-8">
                <h1 className="text-2xl font-bold text-white mb-2">Dashboard</h1>
                <p className="text-slate-400">Welcome back, {admin?.name}</p>
            </div>

            {/* Stats Grid */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <Card className="bg-slate-800 border-slate-700">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-slate-400 text-sm">Total Users</p>
                                <p className="text-3xl font-bold text-white">{stats.users.total}</p>
                            </div>
                            <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                                <Users className="w-6 h-6 text-blue-500" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="bg-slate-800 border-slate-700">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-slate-400 text-sm">MediProviders</p>
                                <p className="text-3xl font-bold text-white">{stats.providers.total}</p>
                            </div>
                            <div className="w-12 h-12 rounded-xl bg-teal-500/20 flex items-center justify-center">
                                <UserCheck className="w-6 h-6 text-teal-500" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="bg-slate-800 border-slate-700">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-slate-400 text-sm">Pending Review</p>
                                <p className="text-3xl font-bold text-amber-500">{stats.providers.pending}</p>
                            </div>
                            <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
                                <Clock className="w-6 h-6 text-amber-500" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="bg-slate-800 border-slate-700">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-slate-400 text-sm">Approved</p>
                                <p className="text-3xl font-bold text-green-500">{stats.providers.approved}</p>
                            </div>
                            <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
                                <CheckCircle className="w-6 h-6 text-green-500" />
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Compliance Overview */}
            <div className="grid lg:grid-cols-2 gap-6 mb-8">
                <Card className="bg-slate-800 border-slate-700">
                    <CardHeader>
                        <CardTitle className="text-white flex items-center gap-2">
                            <Activity className="w-5 h-5 text-indigo-500" />
                            Compliance Status
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="w-3 h-3 rounded-full bg-green-500" />
                                    <span className="text-slate-300">Green (Compliant)</span>
                                </div>
                                <span className="text-2xl font-bold text-green-500">{stats.compliance.green}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="w-3 h-3 rounded-full bg-amber-500" />
                                    <span className="text-slate-300">Amber (Expiring Soon)</span>
                                </div>
                                <span className="text-2xl font-bold text-amber-500">{stats.compliance.amber}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="w-3 h-3 rounded-full bg-red-500" />
                                    <span className="text-slate-300">Red (Non-Compliant)</span>
                                </div>
                                <span className="text-2xl font-bold text-red-500">{stats.compliance.red}</span>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="bg-slate-800 border-slate-700">
                    <CardHeader>
                        <CardTitle className="text-white flex items-center gap-2">
                            <TrendingUp className="w-5 h-5 text-indigo-500" />
                            Recent Registrations
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            {stats.recent_registrations?.map((provider) => (
                                <div key={provider.id} className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                                    <div>
                                        <p className="text-white font-medium">{provider.name}</p>
                                        <p className="text-xs text-slate-400 capitalize">{provider.profession}</p>
                                    </div>
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                        provider.status === 'approved' ? 'bg-green-500/20 text-green-500' :
                                        provider.status === 'pending_review' ? 'bg-amber-500/20 text-amber-500' :
                                        provider.status === 'pending_payment' ? 'bg-blue-500/20 text-blue-500' :
                                        'bg-red-500/20 text-red-500'
                                    }`}>
                                        {provider.status?.replace('_', ' ')}
                                    </span>
                                </div>
                            ))}
                            {(!stats.recent_registrations || stats.recent_registrations.length === 0) && (
                                <p className="text-slate-500 text-center py-4">No recent registrations</p>
                            )}
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Quick Actions */}
            <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                    <CardTitle className="text-white">Quick Actions</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="flex flex-wrap gap-3">
                        <Link to="/admin/providers?status=pending_review">
                            <Button className="bg-amber-600 hover:bg-amber-700">
                                <Clock className="w-4 h-4 mr-2" />
                                Review Pending ({stats.providers.pending})
                            </Button>
                        </Link>
                        <Link to="/admin/providers">
                            <Button variant="outline" className="border-amber-500 text-amber-500 hover:bg-amber-500/10">
                                <AlertTriangle className="w-4 h-4 mr-2" />
                                Expiring Documents ({stats.compliance.amber})
                            </Button>
                        </Link>
                    </div>
                </CardContent>
            </Card>
        </>
    );
}
